import { FlexLayout, H2 } from '@deity/falcon-ui';
import { mapRecordToArray } from '@deity-io/di-data-basics';
import { Package } from '@deity-io/di-data-flows';
import { Collapsible, PackagePopoutSkeleton } from 'components';
import { CACHE_FIRST } from 'contracts/constants';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useGetFlowsByPackageVersionLazyQuery, useGetPackageVersionLazyQuery } from 'services/graphql/generated/hooks';
import type { FlowEntityType } from 'services/graphql/generated/schemas';
import { validate as validateUuid } from 'uuid';
import type { IClusterStatePackage, IRuntimeState } from '../IRuntimeState';
import { PackagePopoutFlow } from './PackagePopoutFlow';
import { PackageVersionBadge } from './PackageVersionBadge';

type PackagePopoutVersionProps = {
  packageVersionKey: string;
  accountUuid: string;
  packageVersion: IClusterStatePackage['versions'][0];
  services: IRuntimeState['services'];
};
export const PackagePopoutVersion: FC<PackagePopoutVersionProps> = ({
  packageVersionKey,
  packageVersion,
  accountUuid,
  services
}) => {
  const [getPackageVersion, { loading: packageVersionLoading, data: packageVersionData }] =
    useGetPackageVersionLazyQuery(CACHE_FIRST);

  const [getFlows, { loading: flowsLoading, data: flowsData }] = useGetFlowsByPackageVersionLazyQuery(CACHE_FIRST);
  useEffect(() => {
    if (packageVersionKey && validateUuid(packageVersionKey)) {
      getPackageVersion({
        variables: {
          uuid: packageVersionKey
        }
      });
      getFlows({
        variables: {
          packageVersionUuid: packageVersionKey,
          offset: 0,
          limit: 500
        }
      });
    }
  }, [packageVersionKey]);
  let pipelineCount = 0;

  return packageVersionLoading ? (
    <PackagePopoutSkeleton />
  ) : (
    <FlexLayout flexDirection="column" width="full" style={{ paddingLeft: '40px' }}>
      <Collapsible
        collapsed={false}
        title={
          <FlexLayout flexDirection="row" width="full">
            <H2>Version</H2>
            <PackageVersionBadge detailed packageVersionKey={packageVersionKey} />
          </FlexLayout>
        }
      >
        {mapRecordToArray(packageVersion.pipelines, (pipeline, pipelineKey) => {
          pipelineCount += 1;

          const flowKey = Package.flowKeyToUuid(pipeline.uuid);
          const flowData = flowsData?.getFlowsByPackageVersion.data.find(flow => flow.uuid === flowKey);

          return (
            <PackagePopoutFlow
              key={pipelineKey}
              pipeState={pipeline}
              flowData={flowData as FlowEntityType}
              accountUuid={accountUuid}
              packageVersionUuid={packageVersionKey}
              services={services}
            />
          );
        })}
        {pipelineCount === 0 ? <H2>Disposed</H2> : null}
      </Collapsible>
    </FlexLayout>
  );
};
